import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthService, CustomerService } from '@core';
import { ProductsService } from 'app/products/products.service';
import { environment } from '../../../environments/environment';
import { Customer } from '@models/customer';
import { Product } from '@models/product';

const confirmationLogoutTextForPrivatePerson =
    'Hinweis: Du bist mit dem Kundenkonto einer Privatperson im Kundenportal angemeldet, die aktuell gewählte Versicherung ist aber nur für gewerbliche Kunden verfügbar.\n\nBitte bestätige, ob du fortfahren möchtest. Du wirst dann vom Kundenportal abgemeldet.';
const confirmationLogoutTextForCompany =
    'Hinweis: Du bist mit dem Kundenkonto eines Vereins, einer Schule oder einer Firma im Kundenportal angemeldet, die aktuell gewählte Versicherung ist aber nur für Privatpersonen verfügbar.\n\nBitte bestätige, ob du fortfahren möchtest. Du wirst dann vom Kundenportal abgemeldet.';

enum ProductAccess {
    canAccess,
    userWishesToLogout,
    cannotAccess,
}

export const productAccessGuard: CanActivateFn = (route) => {
    const router = inject(Router);

    const slug = route.paramMap.get('productSlugOrId');
    if (!slug) {
        redirectToHomePage(router);
        return false;
    }
    const product = inject(ProductsService).getBySlug(slug);
    const user = inject(CustomerService).customer;

    const userAccess = canUserAccessProduct(user, product);

    if (userAccess === ProductAccess.userWishesToLogout) {
        inject(AuthService).logout();
        return true;
    }

    if (userAccess === ProductAccess.cannotAccess) {
        redirectToHomePage(router);
        return false;
    }
    return true;
};

const canUserAccessProduct = (user: Customer, product: Product): ProductAccess => {
    if (user == null) {
        return ProductAccess.canAccess;
    }

    const userIsCompany = user?.title.id === environment.titleCompanyId;
    const productIsCompanyOnly = product?.orderConfig.companiesOnly;
    const productIsPersonOnly = product?.orderConfig.naturalPersonsOnly;

    if (productIsCompanyOnly && !userIsCompany) {
        return logoutPrompt(confirmationLogoutTextForPrivatePerson);
    }

    if (productIsPersonOnly && userIsCompany) {
        return logoutPrompt(confirmationLogoutTextForCompany);
    }

    return ProductAccess.canAccess;
};

const logoutPrompt = (text: string): ProductAccess => {
    const confirmation = window.confirm(text);
    return confirmation === true ? ProductAccess.userWishesToLogout : ProductAccess.cannotAccess;
};

const redirectToHomePage = (router: Router): void => {
    router.navigate(['']);
};
